import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { BetterDocRecommendationDetails } from "@/modules/admin/better-doc/components/better-doc-recommendation-details"
import { BetterDocLiveRecommendationState } from "@/modules/admin/better-doc/types/BetterDocLiveRecommendationState"

type BetterDocLiveRecommendationCardProps = {
  recommendationId: string | null
  liveRecommendationState: BetterDocLiveRecommendationState
  onLoad: (recommendationId: string) => void
}

export const BetterDocLiveRecommendationCard = ({
  recommendationId,
  liveRecommendationState,
  onLoad,
}: BetterDocLiveRecommendationCardProps) => {
  const { t } = useTranslation()

  const handleLoad = () => {
    if (recommendationId !== null) {
      onLoad(recommendationId)
    }
  }

  const renderLiveState = () => {
    switch (liveRecommendationState.status) {
      case "idle":
        return null
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-live-recommendation-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded":
        return <BetterDocRecommendationDetails result={liveRecommendationState.data} />
    }
  }

  return (
    <Card data-testid="better-doc-admin-live-recommendation">
      <CardHeader>
        <CardHeaderTitle>{t("admin.betterDoc.liveRecommendation.title")}</CardHeaderTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-4 p-4 pt-0">
        <div className="flex flex-wrap items-center gap-4">
          <Button
            type="button"
            variant="secondary"
            disabled={recommendationId === null || liveRecommendationState.status === "loading"}
            onClick={handleLoad}
            data-testid="better-doc-admin-live-recommendation-load-button">
            {t("admin.betterDoc.liveRecommendation.load")}
          </Button>
          {recommendationId === null ? (
            <span className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-live-recommendation-hint">
              {t("admin.betterDoc.noRecommendationYet")}
            </span>
          ) : null}
        </div>
        {renderLiveState()}
      </CardBody>
    </Card>
  )
}
